/**
 * The drag-and-drop vocabulary for the roster grid.
 *
 * Two things can be picked up and one thing can be dropped on, so the payloads
 * are declared here once and read through guards rather than by poking at
 * `source.data['type']` in five places. Pragmatic drag and drop hands data
 * around as a loose record; these guards are what turns it back into something
 * the mutation layer can trust.
 */
import type { Hhmm, IsoDate } from '../lib/time.ts';

export const TEMPLATE_DRAG = 'sp-template';
export const SHIFT_DRAG = 'sp-shift';
export const CELL_DROP = 'sp-cell';

export interface TemplateDrag {
  type: typeof TEMPLATE_DRAG;
  templateId: string;
  name: string;
  startTime: Hhmm;
  endTime: Hhmm;
  colour: string | null;
}

export interface ShiftDrag {
  type: typeof SHIFT_DRAG;
  shiftId: string;
  fromUserId: string | null;
  fromAreaId: string | null;
  fromDate: IsoDate;
  locked: boolean;
}

export interface CellDrop {
  type: typeof CELL_DROP;
  /** null on the Open Shifts row: a shift dropped there is unassigned. */
  userId: string | null;
  /** null in the People view and in the Unassigned area group. */
  areaId: string | null;
  date: IsoDate;
  /** The person marked this day unavailable. The drop is not silently
   *  swallowed; it opens the drawer with the conflict stated and a force. */
  blocked: boolean;
}

export type DragPayload = TemplateDrag | ShiftDrag;

const asRecord = (v: unknown): Record<string, unknown> => (v ?? {}) as Record<string, unknown>;

export function isTemplateDrag(data: unknown): data is TemplateDrag {
  return asRecord(data)['type'] === TEMPLATE_DRAG;
}

export function isShiftDrag(data: unknown): data is ShiftDrag {
  return asRecord(data)['type'] === SHIFT_DRAG;
}

export function isCellDrop(data: unknown): data is CellDrop {
  return asRecord(data)['type'] === CELL_DROP;
}

export function isRosterDrag(data: unknown): data is DragPayload {
  return isTemplateDrag(data) || isShiftDrag(data);
}

export function cellKey(userId: string | null, areaId: string | null, date: string): string {
  return `${userId ?? 'open'}|${areaId ?? 'none'}|${date}`;
}
